import { useState } from 'react';
import {
	Box,
	Typography,
	TextField,
	Paper,
	Stack,
	Alert,
	Button,
	InputAdornment,
	Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Actions from '../../components/Actions';
import { Navigate, useOutletContext, useRevalidator } from 'react-router';
import HandymanIcon from '@mui/icons-material/Handyman';
import CheckIcon from '@mui/icons-material/Check';
import CircleIcon from '@mui/icons-material/Circle';
import { authedApiFetch } from '../../utils/Api';

const TenantAdmin = () => {
	// React Router hooks
	const { tenant, user } = useOutletContext();
	const revalidator = useRevalidator();

	const [salesforceClientId, setSalesforceClientId] = useState(
		tenant.salesforce_eca_client_id || ''
	);
	const [salesforceClientSecret, setSalesforceClientSecret] = useState(
		tenant.salesforce_eca_client_secret || ''
	);
	const [salesforceOrgDomain, setSalesforceOrgDomain] = useState(
		(tenant.salesforce_org_url || '').replace(/^https?:\/\//, '')
	);
	const [salesforceOrgId, setSalesforceOrgId] = useState(
		tenant.salesforce_org_id || ''
	);
	const [setupLoading, setSetupLoading] = useState(false);
	const [setupError, setSetupError] = useState(null);
	const [isEditing, setIsEditing] = useState(false);

	const [orgIdLoading, setOrgIdLoading] = useState(false);
	const [orgIdError, setOrgIdError] = useState(null);
	const [isEditingOrgId, setIsEditingOrgId] = useState(false);

	const handleSetupSalesforceEca = async (e) => {
		e.preventDefault();
		setSetupLoading(true);
		setSetupError(null);

		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenant.id}/setup_salesforce_eca`,
				method: 'POST',
				payload: {
					salesforce_eca_client_id: salesforceClientId,
					salesforce_eca_client_secret: salesforceClientSecret,
					salesforce_org_url: salesforceOrgDomain
						? `https://${salesforceOrgDomain}`
						: '',
				},
			});
			revalidator.revalidate();
			setIsEditing(false);
		} catch (err) {
			setSetupError(err.message);
		} finally {
			setSetupLoading(false);
		}
	};

	const handleUpdateOrgId = async (e) => {
		e.preventDefault();
		setOrgIdLoading(true);
		setOrgIdError(null);

		try {
			await authedApiFetch({
				endpoint: `/tenants/${tenant.id}`,
				method: 'PATCH',
				payload: {
					tenant: {
						salesforce_org_id: salesforceOrgId,
					},
				},
			});
			revalidator.revalidate();
			setIsEditingOrgId(false);
		} catch (err) {
			setOrgIdError(err.message);
		} finally {
			setOrgIdLoading(false);
		}
	};

	// handler passed to action component, called after confirming action
	const handleCreateUserPool = async () => {
		await authedApiFetch({
			endpoint: `/tenants/${tenant.id}/user_pool`,
			method: 'POST',
		});

		// imperatively revalidate b/c not using React Router action.
		revalidator.revalidate();
	};

	// Array of actions passed to Action component
	const actions = [
		{
			name: 'createUserPool',
			label: 'Create User Pool and App Clients',
			icon: <HandymanIcon />,
			disabledIcon: <CheckIcon />,
			confirmText:
				'This will create a user pool and app clients for the current tenant.',
			disabledCheck: () => !!tenant.user_pool_id,
			actionHandler: handleCreateUserPool,
		},
	];

	// Route back to marketplace if non-admin user attempts to navigate directly
	if (user.role != 'altvia_admin' && user.role != 'altvia_power_user') {
		return <Navigate to={`/tenants/${tenant.id}/marketplace`} />;
	} else {
		return (
			<>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						rowGap: '16px',
						height: '100vh',
						paddingTop: '12px',
						overflowY: 'auto',
						pb: 4,
					}}
				>
					<Typography variant='h4'>Tenant Admin</Typography>
					<Actions actions={actions} />

					{user.role === 'altvia_admin' && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							<Paper sx={{ p: 3, mt: 4, maxWidth: '800px' }}>
								<Box
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										mb: 2,
									}}
								>
									<Typography variant='h6' gutterBottom>
										Salesforce ECA Integration
									</Typography>
									<Button
										variant={isEditing ? 'contained' : 'outlined'}
										color='error'
										size='small'
										onClick={() => setIsEditing(!isEditing)}
										disabled={setupLoading}
									>
										{isEditing ? 'Cancel' : 'Edit'}
									</Button>
								</Box>
								<Typography variant='body2' color='text.secondary'>
									Register this Tenant&apos;s Salesforce External Client App
									credentials to enable user-level SSO to the connected
									Salesforce org. After creating an ECA in Salesforce, these
									credentials can be found in Salesforce Setup -&gt; External
									Client App Manager.
								</Typography>

								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'flex-start',
										my: 3,
										gap: 1,
									}}
								>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<CircleIcon
											sx={{
												fontSize: 14,
												color: tenant.salesforce_eca_configured
													? 'success.main'
													: 'error.main',
											}}
										/>
										<Typography variant='body2' sx={{ fontWeight: 600 }}>
											Status:{' '}
											{tenant.salesforce_eca_configured
												? 'Configured'
												: 'Not Configured'}
										</Typography>
									</Box>
									{tenant.salesforce_eca_configured && (
										<Typography variant='caption' color='text.secondary'>
											Credentials have been registered with Composio. To verify
											the integration, go to the Connections feature and attempt
											to &apos;Connect&apos; a Salesforce account. Valid
											credentials will successfully redirect you to the
											Salesforce login page.
										</Typography>
									)}
									{tenant.salesforce_auth_config_id && (
										<Typography variant='caption' color='text.secondary'>
											Composio Auth Config ID:{' '}
											{tenant.salesforce_auth_config_id}
										</Typography>
									)}
								</Box>

								<form onSubmit={handleSetupSalesforceEca}>
									<Stack spacing={2}>
										<TextField
											label='Consumer Key'
											type='password'
											variant='outlined'
											size='small'
											sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
											value={salesforceClientId}
											onChange={(e) => setSalesforceClientId(e.target.value)}
											required
											disabled={setupLoading || !isEditing}
										/>
										<TextField
											label='Consumer Secret'
											type='password'
											variant='outlined'
											size='small'
											sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
											value={salesforceClientSecret}
											onChange={(e) =>
												setSalesforceClientSecret(e.target.value)
											}
											required
											disabled={setupLoading || !isEditing}
										/>
										<TextField
											label='Salesforce Org URL'
											variant='outlined'
											size='small'
											placeholder='your-domain.my.salesforce.com'
											helperText='Found in Salesforce Setup → My Domain'
											value={salesforceOrgDomain}
											onChange={(e) => setSalesforceOrgDomain(e.target.value)}
											required
											disabled={setupLoading || !isEditing}
											slotProps={{
												input: {
													startAdornment: (
														<InputAdornment
															position='start'
															sx={{ marginRight: '0.1rem' }}
														>
															https://
														</InputAdornment>
													),
												},
											}}
										/>
										{setupError && (
											<Alert severity='error' size='small'>
												{setupError}
											</Alert>
										)}
										<Box sx={{ alignSelf: 'center' }}>
											<LoadingButton
												type='submit'
												variant='contained'
												loading={setupLoading}
												disabled={!isEditing}
											>
												{tenant.salesforce_eca_configured
													? 'Update Credentials'
													: 'Connect to Salesforce'}
											</LoadingButton>
										</Box>
									</Stack>
								</form>
							</Paper>
							<Paper sx={{ p: 3, mt: 4, maxWidth: '800px' }}>
								<Box>
									<Box
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center',
											mb: 2,
										}}
									>
										<Typography variant='h6' gutterBottom>
											AIM Request Integration
										</Typography>
										<Button
											variant={isEditingOrgId ? 'contained' : 'outlined'}
											color='error'
											size='small'
											onClick={() => setIsEditingOrgId(!isEditingOrgId)}
											disabled={orgIdLoading}
										>
											{isEditingOrgId ? 'Cancel' : 'Edit'}
										</Button>
									</Box>

									<Divider sx={{ my: 2 }} />
									<Typography
										variant='subtitle2'
										sx={{ fontWeight: 700, mb: 2 }}
									>
										18-character Salesforce Org ID
									</Typography>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 2,
											color: 'text.secondary',
										}}
									>
										<Typography variant='body2'>
											AIM requests to Launchpad come with an <b>18-character</b>{' '}
											Salesforce org ID.
										</Typography>

										<Typography variant='body2'>
											To ensure that AIM requests from this Tenant&apos;s
											Salesforce org are directed to it, make sure that it has
											the correct 18-character Salesforce ID. This ID isn&apos;t
											the same one that&apos;s on the Salesforce Setup Company
											Information page - that&apos;s the 15-character ID.
										</Typography>

										<Box>
											<Typography variant='body2' sx={{ mb: 1 }}>
												The 18-character ID can be retrieved by following these
												steps:
											</Typography>
											<Box
												component='ol'
												sx={{
													m: 0,
													pl: 3,
													'& li': {
														fontSize: '0.875rem',
														mb: 0.5,
													},
												}}
											>
												<li>Log into your Salesforce org.</li>
												<li>Navigate to Setup.</li>
												<li>Open the Developer Console.</li>
												<li>Open Debug → Open Execute Anonymous Window.</li>
												<li>
													Execute this code:
													<Box
														component='code'
														sx={{
															display: 'block',
															mt: 1,
															mb: 1,
															p: 1.5,
															backgroundColor: 'background.subtle',
															borderRadius: '4px',
															fontWeight: 700,
															fontSize: '0.8rem',
															fontFamily: 'monospace',
															border: '1px solid',
															borderColor: 'border.light',
														}}
													>
														System.debug(&apos;18-char Org ID: &apos; +
														UserInfo.getOrganizationId());
													</Box>
												</li>
												<li>
													In the Logs tab at the bottom of the Developer
													Console, a new log should be available. Open it,
													enable the &quot;Debug Only&quot; checkbox, and look
													for the line that says &quot;18-char Org ID:
													[ID]&quot;.
												</li>
											</Box>
										</Box>

										<form onSubmit={handleUpdateOrgId}>
											<Stack spacing={2}>
												<TextField
													label='18-character Salesforce Org ID'
													variant='outlined'
													size='small'
													value={salesforceOrgId}
													onChange={(e) => setSalesforceOrgId(e.target.value)}
													required
													disabled={orgIdLoading || !isEditingOrgId}
													error={
														salesforceOrgId.length > 0 &&
														salesforceOrgId.length !== 18
													}
													helperText={
														salesforceOrgId.length > 0 &&
														salesforceOrgId.length !== 18
															? `Must be exactly 18 characters (currently ${salesforceOrgId.length})`
															: ''
													}
													slotProps={{
														htmlInput: {
															maxLength: 18,
														},
													}}
												/>
												{orgIdError && (
													<Alert severity='error' size='small'>
														{orgIdError}
													</Alert>
												)}
												<Box sx={{ alignSelf: 'center' }}>
													<LoadingButton
														type='submit'
														variant='contained'
														loading={orgIdLoading}
														disabled={
															!isEditingOrgId || salesforceOrgId.length !== 18
														}
													>
														Update Org ID
													</LoadingButton>
												</Box>
											</Stack>
										</form>
										<Divider sx={{ my: 2 }} />
									</Box>
								</Box>
							</Paper>
						</Box>
					)}
				</Box>
			</>
		);
	}
};

export default TenantAdmin;
